import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const noRetryOn4xx = (count, error) => !(error?.status >= 400 && error?.status < 500) && count < 2;

export const useMe = () => useQuery({ queryKey: ["me"], queryFn: () => api.get("/me"), staleTime: 30_000, retry: noRetryOn4xx });

export const useConversations = (q = "", limit = 50) =>
  useQuery({
    queryKey: ["conversations", q, limit],
    queryFn: () => api.get(`/conversations?limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: noRetryOn4xx,
  });

export const useConversation = (id, { poll = false } = {}) =>
  useQuery({
    queryKey: ["conversation", id],
    queryFn: () => api.get(`/conversations/${id}`),
    enabled: Boolean(id),
    retry: noRetryOn4xx,
    staleTime: 10_000,
    refetchInterval: (query) => (poll && query.state.data?.messages?.some((m) => m.status === "streaming") ? 2500 : false),
  });

export const useUsage = () =>
  useQuery({ queryKey: ["usage"], queryFn: () => api.get(`/usage/summary?tz=${encodeURIComponent(tz)}`), retry: noRetryOn4xx });

export const usePlans = () => useQuery({ queryKey: ["plans"], queryFn: () => api.get("/plans"), staleTime: 300_000 });

export function useRenameConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }) => api.patch(`/conversations/${id}`, { title }),
    onSuccess: (data) => {
      qc.setQueryData(["conversation", data.id], (old) => (old ? { ...old, conversation: { ...old.conversation, title: data.title } } : old));
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del(`/conversations/${id}`),
    onSuccess: (_d, id) => {
      qc.removeQueries({ queryKey: ["conversation", id] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch) => api.patch("/me/settings", patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: ["me"] });
      const previous = qc.getQueryData(["me"]);
      qc.setQueryData(["me"], (old) => (old ? { ...old, settings: { ...old.settings, ...patch } } : old));
      return { previous };
    },
    onError: (_e, _p, ctx) => ctx?.previous && qc.setQueryData(["me"], ctx.previous),
    onSettled: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (display_name) => api.patch("/me/profile", { display_name }),
    onSuccess: (data) => qc.setQueryData(["me"], (old) => (old ? { ...old, profile: { ...old.profile, ...data } } : old)),
  });
}

const anyProcessing = (items) => items?.some((f) => f?.status === "queued" || f?.status === "processing");

export const useFiles = ({ q = "", kind = "", ready = false, limit = 100 } = {}) =>
  useQuery({
    queryKey: ["files", q, kind, ready, limit],
    queryFn: () => api.get(`/files?limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ""}${kind ? `&kind=${kind}` : ""}${ready ? "&ready=true" : ""}`),
    placeholderData: keepPreviousData,
    retry: noRetryOn4xx,
    refetchInterval: (query) => (anyProcessing(query.state.data?.items) ? 2000 : false),
  });

export const useFile = (id) =>
  useQuery({
    queryKey: ["file", id],
    queryFn: () => api.get(`/files/${id}`),
    enabled: Boolean(id),
    retry: noRetryOn4xx,
    refetchInterval: (query) => (anyProcessing([query.state.data?.file]) ? 1500 : false),
  });

function useFileMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["files"] });
      qc.invalidateQueries({ queryKey: ["file", vars?.id ?? vars] });
    },
  });
}

export const useRenameFile = () => useFileMutation(({ id, name }) => api.patch(`/files/${id}`, { name }));
export const useDeleteFile = () => useFileMutation((id) => api.del(`/files/${id}`));
export const useReprocessFile = () => useFileMutation((id) => api.post(`/files/${id}/reprocess`));

export const useProjects = (status = "active") =>
  useQuery({ queryKey: ["projects", status], queryFn: () => api.get(`/projects?status=${status}`), retry: noRetryOn4xx, staleTime: 15_000 });

export const useProject = (id) =>
  useQuery({
    queryKey: ["project", id],
    queryFn: () => api.get(`/projects/${id}`),
    enabled: Boolean(id),
    retry: noRetryOn4xx,
    refetchInterval: (query) => (anyProcessing(query.state.data?.files) ? 2000 : false),
  });

function useProjectMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project"] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["conversation"] });
    },
  });
}

export const useCreateProject = () => useProjectMutation((body) => api.post("/projects", body));
export const useUpdateProject = () => useProjectMutation(({ id, ...patch }) => api.patch(`/projects/${id}`, patch));
export const useDeleteProject = () => useProjectMutation((id) => api.del(`/projects/${id}`));
export const useAddProjectFiles = () => useProjectMutation(({ id, fileIds }) => api.post(`/projects/${id}/files`, { file_ids: fileIds }));
export const useRemoveProjectFile = () => useProjectMutation(({ id, fileId }) => api.del(`/projects/${id}/files/${fileId}`));
export const useMoveConversation = () => useProjectMutation(({ id, projectId }) => api.patch(`/conversations/${id}/project`, { project_id: projectId }));

const anyRunning = (items) => items?.some((s) => s.status === "running");

export const useResearchSessions = () =>
  useQuery({
    queryKey: ["research-sessions"],
    queryFn: () => api.get("/research/sessions"),
    retry: noRetryOn4xx,
    staleTime: 10_000,
    refetchInterval: (query) => (anyRunning(query.state.data?.items) ? 4000 : false),
  });

export const useResearchSession = (id) =>
  useQuery({ queryKey: ["research-session", id], queryFn: () => api.get(`/research/sessions/${id}`), enabled: Boolean(id), retry: noRetryOn4xx });

function useResearchMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => ["research-sessions", "research-session", "me", "files", "projects", "project"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
  });
}

export const useStartResearch = () =>
  useResearchMutation(({ question, depth, projectId }) => api.post("/research/sessions", { question, depth, ...(projectId ? { project_id: projectId } : {}) }));
export const useStopResearch = () => useResearchMutation((id) => api.post(`/research/sessions/${id}/stop`));
export const useDeleteResearch = () => useResearchMutation((id) => api.del(`/research/sessions/${id}`));
export const useSaveResearch = () => useResearchMutation(({ id, projectId }) => api.post(`/research/sessions/${id}/save`, { project_id: projectId }));
